/**
 * MoveToWorkspaceDialog (FRIDAY-39) — move a board, or a page with all its
 * sub-pages, to another workspace.
 *
 * Steps: pick an eligible destination (member role or higher, not archived,
 * not the current one), pick a parent page for page moves, read the preview
 * (what moves, who loses access, what is cleared or detached), confirm.
 * Confirming the preview is the only confirmation; the server refuses a
 * stale preview with 409 and the dialog then shows the fresh one.
 *
 * Same modal pattern as ConfirmDialog: scrim + card, role="dialog",
 * aria-modal, Escape closes when idle. Native radio inputs and a native
 * <select> keep it keyboard and phone friendly.
 */

import { useEffect, useMemo, useRef, useState } from 'react'
import { ApiError } from '@/lib/api'
import { useWorkspaces } from '@/features/workspaces/workspaces'
import { usePageTree } from '@/features/notebook/pages'
import {
  useExecuteTransfer,
  useTransferPreview,
  type TransferKind,
  type TransferPreview,
  type TransferResult,
} from '@/features/transfers/transfers'
import type { Page } from '@friday/shared/realtime'

export interface MoveToWorkspaceDialogProps {
  kind: TransferKind
  id: string
  /** Board or page name, for the title. */
  label: string
  sourceWorkspaceId: string
  onClose: () => void
  onMoved: (result: TransferResult) => void
}

const COUNT_LABELS: Record<string, string> = {
  tasks: 'tasks',
  subtasks: 'subtasks',
  columns: 'columns',
  sprints: 'sprints',
  milestones: 'milestones',
  labels: 'labels',
  comments: 'comments',
  attachments: 'attachments',
  links: 'task links inside the board',
  githubIntegrations: 'GitHub integration',
  listDefaults: 'board list layout',
  listPreferences: 'personal list layouts',
  activities: 'activity entries',
  pages: 'pages',
  archivedPages: 'archived sub-pages',
  revisions: 'page revisions',
}
const REMOVAL_LABELS: Record<string, string> = {
  assigneesCleared: 'assignees cleared (no access in the destination)',
  boardMembersRemoved: 'board members removed',
  watchersRemoved: 'watchers removed',
  listPreferencesRemoved: 'personal list layouts removed',
  pendingInvitesRevoked: 'pending board invites revoked',
}
const DETACH_LABELS: Record<string, string> = {
  taskLinks: 'task links to tasks outside the board',
  taskPageReferences: 'task and page references',
  pageLabels: 'page labels from other workspaces',
  crossBoardTaskLabels: 'labels from other workspaces',
  crossBoardSprintTasks: 'sprint entries from other boards',
  crossBoardParents: 'parent links to other boards',
  crossBoardTaskFields: 'sprint or milestone links to other boards',
}

function nonZero(record: Record<string, number>, labels: Record<string, string>) {
  return Object.entries(record)
    .filter(([, n]) => n > 0)
    .map(([k, n]) => ({ key: k, n, label: labels[k] ?? k }))
}

/** Destination pages as indented options, live pages only. */
function parentOptions(pages: Page[] | undefined) {
  const live = (pages ?? []).filter((p) => !p.archivedAt).sort((a, b) => a.position - b.position)
  const byParent = new Map<string | null, Page[]>()
  for (const p of live) {
    const arr = byParent.get(p.parentPageId) ?? []
    arr.push(p)
    byParent.set(p.parentPageId, arr)
  }
  const out: { id: string; title: string; depth: number }[] = []
  const visit = (parent: string | null, depth: number) => {
    for (const p of byParent.get(parent) ?? []) {
      out.push({ id: p.id, title: p.title, depth })
      visit(p.id, depth + 1)
    }
  }
  visit(null, 0)
  return out
}

export function MoveToWorkspaceDialog({
  kind,
  id,
  label,
  sourceWorkspaceId,
  onClose,
  onMoved,
}: MoveToWorkspaceDialogProps) {
  const workspaces = useWorkspaces()
  const [targetId, setTargetId] = useState<string | null>(null)
  const [parentId, setParentId] = useState<string | null>(null)
  const [notice, setNotice] = useState<string | null>(null)
  const [execError, setExecError] = useState<string | null>(null)
  // FRIDAY-105: the labels that move with a board need an explicit accept.
  const [labelsAccepted, setLabelsAccepted] = useState(false)
  // One key per confirmed plan: a retry of the same plan replays safely.
  const idemKey = useRef<{ version: string; key: string } | null>(null)
  const firstFieldRef = useRef<HTMLFieldSetElement>(null)

  const eligible = useMemo(
    () =>
      (workspaces.data ?? []).filter(
        (w) =>
          w.id !== sourceWorkspaceId &&
          !w.archivedAt &&
          (w.role === 'owner' || w.role === 'admin' || w.role === 'member'),
      ),
    [workspaces.data, sourceWorkspaceId],
  )
  const destPages = usePageTree(kind === 'page' && targetId ? targetId : undefined)
  const preview = useTransferPreview(
    kind,
    id,
    targetId ? { targetWorkspaceId: targetId, targetParentPageId: kind === 'page' ? parentId : null } : null,
  )
  const execute = useExecuteTransfer(kind, id)
  const busy = execute.isPending

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && !busy) onClose()
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [busy, onClose])

  useEffect(() => {
    firstFieldRef.current?.querySelector<HTMLElement>('input, button')?.focus()
  }, [workspaces.isSuccess])

  const thing = kind === 'project' ? 'board' : 'page'
  const target = eligible.find((w) => w.id === targetId)

  const confirm = async () => {
    const plan = preview.data
    if (!plan || !targetId) return
    setExecError(null)
    setNotice(null)
    if (idemKey.current?.version !== plan.expectedVersion) {
      idemKey.current = { version: plan.expectedVersion, key: crypto.randomUUID() }
    }
    try {
      const result = await execute.mutateAsync({
        targetWorkspaceId: targetId,
        targetParentPageId: kind === 'page' ? parentId : null,
        expectedVersion: plan.expectedVersion,
        idemKey: idemKey.current.key,
      })
      onMoved(result)
    } catch (e) {
      if (e instanceof ApiError && e.code === 'STALE_PLAN') {
        setNotice('The content changed since this summary was made. Review the updated summary and confirm again.')
        void preview.refetch()
        return
      }
      setExecError(e instanceof ApiError ? e.message : 'The move failed. Nothing was moved.')
    }
  }

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label={`Move ${label} to another workspace`}
      className="fixed inset-0 z-[80] flex items-center justify-center bg-black/40 p-4 backdrop-blur-sm"
      onClick={() => {
        if (!busy) onClose()
      }}
    >
      <div
        className="card flex max-h-[90vh] w-full max-w-lg flex-col overflow-hidden"
        onClick={(e) => e.stopPropagation()}
        data-testid="move-to-workspace"
      >
        <div className="border-b border-surface-200 px-4 py-3 dark:border-surface-700">
          <h2 className="text-base font-semibold">Move “{label}” to another workspace</h2>
          <p className="mt-0.5 text-xs text-surface-500">
            {kind === 'project'
              ? 'The board moves with its tasks, subtasks, columns, sprints, milestones, labels, comments and attachments.'
              : 'The page moves with all its sub-pages and their history.'}{' '}
            People without access to the destination lose access to it.
          </p>
        </div>

        <div className="min-h-0 flex-1 space-y-4 overflow-y-auto px-4 py-3 text-sm">
          <fieldset ref={firstFieldRef}>
            <legend className="mb-1 text-xs font-semibold uppercase tracking-wider text-surface-500">
              Destination workspace
            </legend>
            {workspaces.isLoading && <p className="text-xs text-surface-500">Loading workspaces…</p>}
            {workspaces.isError && (
              <p role="alert" className="text-xs text-red-600">
                Could not load your workspaces.{' '}
                <button type="button" className="underline" onClick={() => workspaces.refetch()}>
                  Retry
                </button>
              </p>
            )}
            {workspaces.isSuccess && eligible.length === 0 && (
              <p className="text-xs text-surface-500">
                You have no other workspace where you can add content. You need member access or higher.
              </p>
            )}
            <div className="space-y-1">
              {eligible.map((w) => (
                <label
                  key={w.id}
                  className="flex min-h-11 cursor-pointer items-center gap-2 rounded-md border border-surface-200 px-3 py-2 hover:bg-surface-50 has-[:checked]:border-brand-500 dark:border-surface-700 dark:hover:bg-surface-800"
                >
                  <input
                    type="radio"
                    name="move-target"
                    value={w.id}
                    checked={targetId === w.id}
                    disabled={busy}
                    onChange={() => {
                      setTargetId(w.id)
                      setParentId(null)
                      setNotice(null)
                      setExecError(null)
                    }}
                  />
                  <span className="min-w-0 flex-1 truncate">{w.name}</span>
                  <span className="text-[10px] uppercase text-surface-500">{w.role}</span>
                </label>
              ))}
            </div>
          </fieldset>

          {kind === 'page' && targetId && (
            <div>
              <label htmlFor="move-parent" className="mb-1 block text-xs font-semibold uppercase tracking-wider text-surface-500">
                Place under
              </label>
              <select
                id="move-parent"
                className="min-h-11 w-full rounded-md border border-surface-300 bg-white px-2 py-1.5 text-sm dark:border-surface-700 dark:bg-surface-900"
                value={parentId ?? ''}
                disabled={busy || destPages.isLoading}
                onChange={(e) => {
                  setParentId(e.target.value || null)
                  setNotice(null)
                  setExecError(null)
                }}
              >
                <option value="">Top level</option>
                {parentOptions(destPages.data).map((p) => (
                  <option key={p.id} value={p.id}>
                    {'  '.repeat(p.depth)}
                    {p.title}
                  </option>
                ))}
              </select>
            </div>
          )}

          {targetId && (
            <section aria-live="polite" aria-busy={preview.isFetching} data-testid="move-preview">
              <h3 className="mb-1 text-xs font-semibold uppercase tracking-wider text-surface-500">Summary</h3>
              {preview.isFetching && <p className="text-xs text-surface-500">Checking what will move…</p>}
              {preview.isError && !preview.isFetching && (
                <div role="alert" className="rounded-md bg-red-50 p-2 text-xs text-red-700 dark:bg-red-900/30 dark:text-red-200">
                  <p>{preview.error instanceof ApiError ? preview.error.message : 'Could not prepare the move.'}</p>
                  <button type="button" className="mt-1 underline" onClick={() => preview.refetch()}>
                    Try again
                  </button>
                </div>
              )}
              {preview.data && !preview.isFetching && (
                <>
                  <PreviewSummary plan={preview.data} thing={thing} />
                  <MovedLabels
                    key={preview.data.expectedVersion}
                    plan={preview.data}
                    accepted={labelsAccepted}
                    onAcceptedChange={setLabelsAccepted}
                    disabled={busy}
                  />
                </>
              )}
            </section>
          )}

          {notice && (
            <p role="status" className="rounded-md bg-amber-50 p-2 text-xs text-amber-800 dark:bg-amber-900/30 dark:text-amber-200">
              {notice}
            </p>
          )}
          {execError && (
            <p role="alert" className="rounded-md bg-red-50 p-2 text-xs text-red-700 dark:bg-red-900/30 dark:text-red-200">
              {execError}
            </p>
          )}
        </div>

        <div className="flex flex-wrap items-center justify-end gap-2 border-t border-surface-200 px-4 py-3 dark:border-surface-700">
          <button type="button" className="btn-ghost min-h-11" onClick={onClose} disabled={busy}>
            Cancel
          </button>
          <button
            type="button"
            className="btn-primary min-h-11"
            onClick={confirm}
            disabled={
              busy ||
              !preview.data ||
              preview.isFetching ||
              preview.isError ||
              (!!preview.data.labels?.length && !labelsAccepted)
            }
          >
            {busy ? 'Moving…' : execError ? 'Retry move' : target ? `Move ${thing} to ${target.name}` : `Move ${thing}`}
          </button>
        </div>
      </div>
    </div>
  )
}

/**
 * The labels a board's tasks use, and how they land in the destination. New
 * labels are created there for the moved board only. The move waits for the
 * user to accept the list.
 */
function MovedLabels({
  plan,
  accepted,
  onAcceptedChange,
  disabled,
}: {
  plan: TransferPreview
  accepted: boolean
  onAcceptedChange: (accepted: boolean) => void
  disabled: boolean
}) {
  const labels = plan.labels ?? []
  // A new preview needs a new accept.
  useEffect(() => onAcceptedChange(false), [plan.expectedVersion])
  if (!labels.length) return null
  const created = labels.filter((l) => l.status === 'new')
  return (
    <div className="mt-3 space-y-2 rounded-md border border-surface-200 p-3 text-xs dark:border-surface-700" data-testid="move-labels">
      <p className="font-medium">Labels move with the board</p>
      <p className="text-surface-500">
        {created.length
          ? `${created.length} ${created.length === 1 ? 'label is' : 'labels are'} created in ${plan.targetWorkspaceName}, available only on this board. `
          : ''}
        Labels that already exist in {plan.targetWorkspaceName} are reused.
      </p>
      <ul className="flex flex-wrap gap-1">
        {labels.map((l) => (
          <li
            key={l.name}
            className="flex items-center gap-1 rounded-full px-2 py-0.5 text-[11px] font-medium"
            style={{ backgroundColor: l.color + '25', color: l.color }}
          >
            {l.name}
            <span className="text-surface-500">· {l.status === 'new' ? 'new' : 'existing'}</span>
          </li>
        ))}
      </ul>
      <label className="flex items-center gap-2">
        <input
          type="checkbox"
          className="h-4 w-4"
          checked={accepted}
          disabled={disabled}
          onChange={(e) => onAcceptedChange(e.target.checked)}
        />
        I understand how these labels move
      </label>
    </div>
  )
}

function PreviewSummary({ plan, thing }: { plan: TransferPreview; thing: string }) {
  const moves = nonZero(plan.counts, COUNT_LABELS)
  const removals = nonZero(plan.removals, REMOVAL_LABELS)
  const detach = nonZero(plan.detach, DETACH_LABELS)
  const users = plan.usersLosingAccess
  return (
    <div className="space-y-3 text-xs">
      <div>
        <p className="font-medium">Moves with the {thing}</p>
        {moves.length ? (
          <ul className="mt-1 list-disc pl-5" data-testid="move-counts">
            {moves.map((m) => (
              <li key={m.key}>
                {m.n} {m.label}
              </li>
            ))}
          </ul>
        ) : (
          <p className="text-surface-500">Only the {thing} itself.</p>
        )}
      </div>
      <div>
        <p className="font-medium">People who lose access</p>
        {users.count ? (
          <>
            <p data-testid="move-users-count">
              {users.count} {users.count === 1 ? 'person has' : 'people have'} no access to {plan.targetWorkspaceName}.
            </p>
            <ul className="mt-1 list-disc pl-5" data-testid="move-users">
              {users.users.slice(0, 10).map((u) => (
                <li key={u.userId}>{u.displayName ?? u.userId}</li>
              ))}
              {users.count > 10 && <li>and {users.count - 10} more</li>}
            </ul>
          </>
        ) : (
          <p className="text-surface-500">Nobody. Everyone with access is a member of {plan.targetWorkspaceName}.</p>
        )}
      </div>
      {removals.length > 0 && (
        <div>
          <p className="font-medium">Cleared because of access</p>
          <ul className="mt-1 list-disc pl-5" data-testid="move-removals">
            {removals.map((m) => (
              <li key={m.key}>
                {m.n} {m.label}
              </li>
            ))}
          </ul>
        </div>
      )}
      {detach.length > 0 && (
        <div>
          <p className="font-medium">Links that cross workspaces are detached</p>
          <ul className="mt-1 list-disc pl-5" data-testid="move-detach">
            {detach.map((m) => (
              <li key={m.key}>
                {m.n} {m.label}
              </li>
            ))}
          </ul>
        </div>
      )}
      {plan.slugRenames.length > 0 && (
        <div>
          <p className="font-medium">Renamed to avoid a clash</p>
          <ul className="mt-1 list-disc pl-5" data-testid="move-renames">
            {plan.slugRenames.map((r) => (
              <li key={r.pageId}>
                {r.from} becomes {r.to}
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  )
}
